import { useState } from "react";

import { AutoResizeTextarea, Button } from "@/components/ui";
import type { TextAttentionAnnotation } from "./text-attention-annotations";
import { useT } from "@/i18n";
import { compactCursorMessages } from "@/i18n/messages/compact-cursor";

export function TextAttentionDraftComments({
  annotations,
  onCommentChange,
}: {
  annotations: TextAttentionAnnotation[];
  onCommentChange: (id: string, comment: string) => void;
}) {
  const t = useT(compactCursorMessages);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  if (annotations.length === 0) return null;
  const selected = annotations.find((item) => item.id === selectedId);
  return (
    <div className="px-2 pb-1" data-testid="text-attention-draft-comments">
      <div className="scrollbar-none flex gap-1 overflow-x-auto">
        {annotations.map((item) => (
          <Button
            key={item.id}
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 gap-1 px-1.5 text-appkit-caption"
            aria-label={t("editAnnotation", { number: item.number })}
            aria-pressed={selectedId === item.id}
            data-testid={`text-attention-draft-annotation-${item.number}`}
            onClick={() =>
              setSelectedId(selectedId === item.id ? null : item.id)
            }
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-[var(--color-surface-selected)] text-primary">
              {item.number}
            </span>
            {item.comment ? (
              <span className="max-w-32 truncate">{item.comment}</span>
            ) : null}
          </Button>
        ))}
      </div>
      {selected ? (
        <AutoResizeTextarea
          autoFocus
          appearance="plain"
          rows={1}
          maxHeight={144}
          className="mt-1 min-h-7 w-full px-2 py-1 text-appkit-caption leading-5"
          value={selected.comment}
          placeholder={t("commentOnAnnotation", { number: selected.number })}
          aria-label={t("commentForAnnotation", { number: selected.number })}
          data-testid={`text-attention-draft-comment-input-${selected.number}`}
          onChange={(event) => onCommentChange(selected.id, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              setSelectedId(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}
